import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Services
import { userService } from '../../services/users';
import { querySearch } from '../../services/search';
import { worksService } from '../../services/works';

// Interfaces
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';
import type { Track, TrackWithSave } from '../../interfaces/track';
import type { LibraryWork } from '../../interfaces/work';
import { RootState } from '../store';
import { mapComposerWorkToLibraryWork } from '../../utils/workMapping';
import { searchDebugLog, searchDebugWarn } from '../../lib/debug/searchLogging';

type Item = Playlist | Album | Track | Artist;

export type SearchSection = 'ALL' | 'ARTISTS' | 'TRACKS' | 'ALBUMS' | 'PLAYLISTS';

type WorksStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type SearchState = {
  top: Item | null;
  songs: TrackWithSave[];
  artists: Artist[];
  albums: Album[];
  playlists: Playlist[];
  requestId: string | null;
  loading: boolean;
  section: SearchSection;
  songsTotal: number;
  works: LibraryWork[];
  worksStatus: WorksStatus;
  worksError: string | null;
  worksRequestId: string | null;
};

const initialState: SearchState = {
  playlists: [],
  songs: [],
  artists: [],
  albums: [],
  top: null,
  requestId: null,
  loading: true,
  section: 'ALL',
  songsTotal: 0,
  works: [],
  worksStatus: 'idle',
  worksError: null,
  worksRequestId: null,
};

const fetchArtists = createAsyncThunk<Artist[], string>('search/fetchArtists', async (query) => {
  const response = await querySearch({ q: query, type: 'artist', limit: 50 });
  return response.data.artists.items;
});

const fetchAlbums = createAsyncThunk<Album[], string>('search/fetchAlbums', async (query) => {
  const response = await querySearch({ q: query, type: 'album', limit: 50 });
  return response.data.albums.items;
});

const fetchPlaylists = createAsyncThunk<Playlist[], string>(
  'search/fetchPlaylists',
  async (query) => {
    const response = await querySearch({ q: query, type: 'playlist', limit: 50 });
    return response.data.playlists.items;
  }
);

const fetchSongs = createAsyncThunk<[TrackWithSave[], number], string>(
  'search/fetchSongs',
  async (query, params) => {
    const response = await querySearch({ q: query, type: 'track', limit: 50 });
    const tracks = response.data.tracks.items;
    const total = response.data.tracks.total;

    const extraRequests = [
      userService.checkSavedTracks(tracks.map((t) => t.id)).catch(() => ({
        data: [],
      })),
    ];

    await Promise.all(extraRequests);

    const saves = (await extraRequests[0]).data;

    const items = tracks.map((track, index) => ({
      ...track,
      saved: saves[index],
    }));

    return [items, total];
  }
);

const fetchMoreSongs = createAsyncThunk<TrackWithSave[], string>(
  'search/fetchMoreSongs',
  async (query, { getState }) => {
    const state = getState() as RootState;
    const { songs } = state.search;

    const response = await querySearch({
      q: query,
      limit: 50,
      type: 'track',
      offset: songs.length,
    });
    const tracks = response.data.tracks.items;

    const extraRequests = [
      userService.checkSavedTracks(tracks.map((t) => t.id)).catch(() => ({
        data: [],
      })),
    ];

    await Promise.all(extraRequests);

    const saves = (await extraRequests[0]).data;

    return tracks.map((track, index) => ({
      ...track,
      saved: saves[index],
    }));
  }
);

const fetchWorks = createAsyncThunk<LibraryWork[], string, { rejectValue: string }>(
  'search/fetchWorks',
  async (query, { rejectWithValue }) => {
    try {
      const works = await worksService.searchWorks(query, 10);
      return works.map(mapComposerWorkToLibraryWork);
    } catch (error: any) {
      const message =
        error?.response?.data?.error ??
        error?.message ??
        'Failed to fetch works';
      if (process.env.NODE_ENV === 'development') {
        console.warn('[searchSlice] fetchWorks failed', { query, error });
      }
      return rejectWithValue(message);
    }
  }
);

export const fetchSearch = createAsyncThunk<
  [Item | null, [TrackWithSave[], number], Artist[], Album[], Playlist[]],
  string
>('search/fetchSearch', async (query) => {
  const promises = [
    querySearch({ q: query, type: 'album,track,artist,playlist', limit: 1 }),
    querySearch({ q: query, type: 'track', limit: 5 }),
    querySearch({ q: query, type: 'album', limit: 10 }),
    querySearch({ q: query, type: 'artist', limit: 10 }),
    querySearch({ q: query, type: 'playlist', limit: 10 }),
  ];

  const responses = await Promise.all(promises);

  const rawTopItems = [
    responses[0]?.data?.artists?.items?.[0],
    responses[0]?.data?.albums?.items?.[0],
    responses[0]?.data?.tracks?.items?.[0],
    responses[0]?.data?.playlists?.items?.[0],
  ];
  const topItems = rawTopItems.filter((item): item is Item => !!item && typeof (item as any).name === 'string');
  const normalizedQuery = (query ?? '').toLowerCase();
  const topItem =
    topItems.find((item) => item.name?.toLowerCase() === normalizedQuery) ||
    topItems.find((item) => item.name?.toLowerCase().includes(normalizedQuery)) ||
    topItems[0] ||
    null;

  const tracks = responses[1].data.tracks.items;
  const tracksTotal = responses[1].data.tracks.total;

  const artists = responses[3].data.artists.items;
  const albums = responses[2].data.albums.items;
  const playlists = responses[4].data.playlists.items;

  const extraRequests = [
    userService.checkSavedTracks(tracks.map((t) => t.id)).catch(() => ({
      data: [],
    })),
  ];

  await Promise.all(extraRequests);

  const saves = (await extraRequests[0]).data;

  const tracksWithSaves: TrackWithSave[] = tracks.map((track, index) => ({
    ...track,
    saved: saves[index],
  }));

  return [topItem, [tracksWithSaves, tracksTotal], artists, albums, playlists];
});

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSection(state, action: PayloadAction<SearchSection>) {
      state.section = action.payload;
    },
    setSavedStateForTrack(
      state,
      action: PayloadAction<{
        id: string;
        saved: boolean;
      }>
    ) {
      const track = state.songs.find((t) => t.id === action.payload.id);
      if (track) {
        track.saved = action.payload.saved;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSearch.pending, (state, action) => {
      state.requestId = action.meta.requestId;
      state.loading = true;
      state.top = null;
      searchDebugLog('fetchSearch.pending', {
        query: action.meta.arg,
        requestId: action.meta.requestId,
      });
    });
    builder.addCase(fetchSearch.fulfilled, (state, action) => {
      if (state.requestId !== action.meta.requestId) {
        searchDebugLog('fetchSearch.fulfilled.skipped_stale', {
          query: action.meta.arg,
          expectedRequestId: state.requestId,
          receivedRequestId: action.meta.requestId,
        });
        return;
      }

      searchDebugLog('fetchSearch.fulfilled', {
        query: action.meta.arg,
        topItem: action.payload?.[0]?.name ?? null,
        artists: action.payload?.[2]?.length ?? 0,
        albums: action.payload?.[3]?.length ?? 0,
        playlists: action.payload?.[4]?.length ?? 0,
        requestId: action.meta.requestId,
      });

      state.top = action.payload[0];

      state.songs = action.payload[1][0];
      state.songsTotal = action.payload[1][1];

      state.artists = action.payload[2];
      state.albums = action.payload[3];
      state.playlists = action.payload[4];
      state.loading = false;
      state.requestId = null;
    });
    builder.addCase(fetchSearch.rejected, (state, action) => {
      if (state.requestId !== action.meta.requestId) {
        searchDebugLog('fetchSearch.rejected.skipped_stale', {
          query: action.meta.arg,
          expectedRequestId: state.requestId,
          receivedRequestId: action.meta.requestId,
        });
        return;
      }

      searchDebugWarn('fetchSearch.rejected', {
        query: action.meta.arg,
        requestId: action.meta.requestId,
        error: action.error?.message,
      });

      state.loading = false;
      state.requestId = null;
    });
    builder.addCase(fetchArtists.fulfilled, (state, action) => {
      state.artists = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAlbums.fulfilled, (state, action) => {
      state.albums = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchPlaylists.fulfilled, (state, action) => {
      state.playlists = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchSongs.fulfilled, (state, action) => {
      state.songs = action.payload[0];
      state.songsTotal = action.payload[1];
      state.loading = false;
    });
    builder.addCase(fetchMoreSongs.fulfilled, (state, action) => {
      state.songs = [...state.songs, ...action.payload];
      state.loading = false;
    });
    builder.addCase(fetchWorks.pending, (state, action) => {
      state.worksRequestId = action.meta.requestId;
      state.worksStatus = 'loading';
      state.works = [];
      state.worksError = null;
      searchDebugLog('fetchWorks.pending', {
        query: action.meta.arg,
        requestId: action.meta.requestId,
      });
    });
    builder.addCase(fetchWorks.fulfilled, (state, action) => {
      if (state.worksRequestId !== action.meta.requestId) {
        searchDebugLog('fetchWorks.fulfilled.skipped_stale', {
          query: action.meta.arg,
          expectedRequestId: state.worksRequestId,
          receivedRequestId: action.meta.requestId,
        });
        return;
      }

      searchDebugLog('fetchWorks.fulfilled', {
        query: action.meta.arg,
        results: action.payload.length,
        requestId: action.meta.requestId,
      });

      state.works = action.payload;
      state.worksStatus = 'succeeded';
      state.worksError = null;
      state.worksRequestId = null;
    });
    builder.addCase(fetchWorks.rejected, (state, action) => {
      if (state.worksRequestId !== action.meta.requestId) {
        searchDebugLog('fetchWorks.rejected.skipped_stale', {
          query: action.meta.arg,
          expectedRequestId: state.worksRequestId,
          receivedRequestId: action.meta.requestId,
        });
        return;
      }

      searchDebugWarn('fetchWorks.rejected', {
        query: action.meta.arg,
        requestId: action.meta.requestId,
        error: action.payload ?? action.error?.message,
      });

      state.works = [];
      state.worksStatus = 'failed';
      state.worksError = action.payload ?? action.error?.message ?? 'Failed to fetch works';
      state.worksRequestId = null;
    });
  },
});

export const searchActions = {
  fetchSearch,
  fetchArtists,
  fetchAlbums,
  fetchPlaylists,
  fetchMoreSongs,
  fetchSongs,
  fetchWorks,
  ...searchSlice.actions,
};

export default searchSlice.reducer;
